import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryCostingPolicy } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-offering-inventory-costing-panel',
  imports: [
    AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, DialogModule,
    InputTextModule, InputNumberModule, SelectModule, CheckboxModule, JalaliDateInputComponent,
    CurrencySelectorComponent, AinCanAccessDirective
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.offerings.inventory.costing.hint.title'"
      [descriptionKey]="'catalog.offerings.inventory.costing.hint.description'" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else if (permissionDenied()) {
      <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
    } @else {
      <div class="toolbar">
        <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
        <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.costing.policies.bootstrapDefault')" (click)="bootstrapDefault()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
      </div>

      <p-table [value]="rows()" [paginator]="rows().length > 15" [rows]="15">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('catalog.costing.policies.method') }}</th>
            <th>{{ t('catalog.inventory.warehouseId') }}</th>
            <th>{{ t('columns.currency') }}</th>
            <th>{{ t('columns.status') }}</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><button type="button" class="link-btn" (click)="openEdit(row)">{{ methodLabel(row.method) }}</button></td>
            <td>{{ resolveWarehouse(row.warehouseId) }}</td>
            <td>{{ row.currencyCode }}</td>
            <td>{{ row.isActive ? t('catalog.costing.policies.active') : t('catalog.costing.policies.inactive') }}</td>
            <td>
              <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="openEdit(row)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (error()) { <p class="error">{{ error() }}</p> }
      @if (message()) { <p class="success">{{ message() }}</p> }
    }

    <p-dialog [(visible)]="editorVisible" [header]="editorIsNew() ? t('catalog.costing.policies.createTitle') : t('catalog.costing.policies.editTitle')" [modal]="true" [style]="{ width: '520px' }">
      <div class="form-grid" ainCanAccess [ainCanAccessPermission]="editorIsNew() ? 'inventory.costing.manage' : 'inventory.costing.policy-elevated'">
        <label>
          <span>{{ t('catalog.costing.policies.method') }}</span>
          <p-select [(ngModel)]="form.method" [options]="methodOptions()" optionLabel="label" optionValue="value" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.inventory.warehouseId') }}</span>
          <p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.costing.policies.effectiveFrom') }}</span>
          <ain-date-input [(ngModel)]="form.effectiveFromUtc" />
        </label>
        <label>
          <span>{{ t('columns.currency') }}</span>
          <ain-currency-selector [(ngModel)]="form.currencyCode" />
        </label>
        <label>
          <span>{{ t('catalog.costing.policies.roundingScale') }}</span>
          <p-inputNumber [(ngModel)]="form.roundingScale" [min]="0" [max]="6" />
        </label>
        <label class="check">
          <p-checkbox [binary]="true" [(ngModel)]="form.hardBlockNegativeStock" />
          <span>{{ t('catalog.costing.policies.hardBlockNegativeStock') }}</span>
        </label>
        <label class="check">
          <p-checkbox [binary]="true" [(ngModel)]="form.isActive" />
          <span>{{ t('catalog.costing.policies.active') }}</span>
        </label>
        @if (!editorIsNew()) {
          <label>
            <span>{{ t('catalog.costing.policies.changeReason') }}</span>
            <input pInputText [(ngModel)]="form.changeReason" />
          </label>
        }
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="editorVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; margin-top: 8px; }
    .success { color: #15803d; margin-top: 8px; }
    .form-grid { display: grid; gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .check { display: flex; align-items: center; gap: 8px; }
  `]
})
export class OfferingInventoryCostingPanelComponent implements OnInit {
  @Input({ required: true }) offeringId!: string;
  @Output() inventoryChanged = new EventEmitter<void>();

  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly permissionDenied = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly rows = signal<InventoryCostingPolicy[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly editorIsNew = signal(true);

  editorVisible = false;
  private editingId = '';
  private originalMethod = '';
  private originalIsActive = true;

  form = {
    method: 'MovingWac',
    warehouseId: '',
    effectiveFromUtc: new Date().toISOString(),
    currencyCode: 'IRR',
    roundingScale: 2,
    hardBlockNegativeStock: true,
    isActive: true,
    changeReason: ''
  };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  methodOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('catalog.costing.methods.MovingWac'), value: 'MovingWac' },
      { label: this.t('catalog.costing.methods.Fifo'), value: 'Fifo' },
      { label: this.t('catalog.costing.methods.Standard'), value: 'Standard' }
    ];
  }

  methodLabel(method: string): string {
    const key = `catalog.costing.methods.${method}`;
    const translated = this.t(key);
    return translated !== key ? translated : method;
  }

  warehouseOptions(): Array<{ label: string; value: string }> {
    return this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  }

  resolveWarehouse(id: string | null): string {
    if (!id) return '—';
    const w = this.warehouses().find(item => item.id === id);
    return w ? `${w.code} — ${w.name}` : id;
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.costingApi.listPolicies({ offeringId: this.offeringId }).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    this.editorIsNew.set(true);
    this.editingId = '';
    this.form = {
      method: 'MovingWac',
      warehouseId: '',
      effectiveFromUtc: new Date().toISOString(),
      currencyCode: 'IRR',
      roundingScale: 2,
      hardBlockNegativeStock: true,
      isActive: true,
      changeReason: ''
    };
    this.editorVisible = true;
  }

  openEdit(row: InventoryCostingPolicy): void {
    this.editorIsNew.set(false);
    this.editingId = row.id;
    this.originalMethod = row.method;
    this.originalIsActive = row.isActive;
    this.form = {
      method: row.method,
      warehouseId: row.warehouseId ?? '',
      effectiveFromUtc: row.effectiveFromUtc,
      currencyCode: row.currencyCode,
      roundingScale: row.roundingScale,
      hardBlockNegativeStock: row.hardBlockNegativeStock,
      isActive: row.isActive,
      changeReason: ''
    };
    this.editorVisible = true;
  }

  save(): void {
    this.saving.set(true);
    this.error.set('');
    const payload = {
      offeringId: this.offeringId,
      warehouseId: this.form.warehouseId || null,
      method: this.form.method,
      effectiveFromUtc: this.form.effectiveFromUtc,
      currencyCode: this.form.currencyCode,
      roundingScale: this.form.roundingScale,
      hardBlockNegativeStock: this.form.hardBlockNegativeStock,
      isActive: this.form.isActive
    };
    const needsElevated = !this.editorIsNew() &&
      (this.form.method !== this.originalMethod || this.form.isActive !== this.originalIsActive);
    if (needsElevated && !this.form.changeReason.trim()) {
      this.saving.set(false);
      this.error.set(this.t('catalog.costing.policies.changeReasonRequired'));
      return;
    }
    const request$ = this.editorIsNew()
      ? this.costingApi.createPolicy(payload)
      : needsElevated
        ? this.costingApi.changePolicyWithAudit(this.editingId, { ...payload, reason: this.form.changeReason.trim() })
        : this.costingApi.updatePolicy(this.editingId, payload);

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.editorVisible = false;
        this.message.set(this.t('catalog.costing.states.saved'));
        this.load(false);
        this.inventoryChanged.emit();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.costing.states.saveFailed'));
      }
    });
  }

  bootstrapDefault(): void {
    this.costingApi.bootstrapDefaultPolicy().subscribe({
      next: result => {
        this.message.set(result.created
          ? this.t('catalog.costing.policies.bootstrapCreated')
          : this.t('catalog.costing.policies.bootstrapExists'));
        this.load(false);
      },
      error: () => this.error.set(this.t('catalog.costing.policies.bootstrapFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
